/* eslint-disable camelcase */
import { Box, Card, Heading, MessageBlock, Paragraph } from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  ERROR_MESSAGE_DETAILS_PAGE__500_ALERT,
  ERROR_MESSAGE_DETAILS_PAGE__ALERT_EXPIRED,
} from '../../../../configurations/error-messages';
import { telemetry } from '../../../../services/backend';
import { getAlertDetails } from '../../../../services/member/alerts';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import ErrorMessage from '../../../shared/ErrorMessage/ErrorMessage';
import {
  getWeatherAlertIcon,
  ReactSvg,
  sendArrow,
} from '../../../shared/vector-images';
import CustomDescription from '../../../shared/_deprecated/components/CustomDescription/CustomDescription';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';

const CardFooter = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.lightgrey};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterLink = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 16px 0;
  cursor: pointer;
`;

const AlertDetailsContainer = ({ id }) => {
  const history = useHistory();
  const theme = useTheme();

  const [data, setData] = useState();
  const [invalid, setInvalid] = useState(false);
  const [errorDescription, setErrorDescription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customDescription, setCustomDescription] = useState([]);

  const sharingPath = `alerts/details/${id}`;

  useEffect(() => {
    let isMounted = true;

    const getData = async () => {
      setIsLoading(true);
      try {
        const remoteData = await getAlertDetails(id);
        telemetry(
          telemetry.DEBUG,
          `[AlertDetailsContainer.getData] received data details: ${JSON.stringify(
            remoteData,
          )}`,
        );
        if (isMounted) {
          setData(remoteData);
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[AlertDetailsContainer.getData] Error retrieving ${JSON.stringify(
            err,
          )}`,
        );
        if (isMounted) {
          setErrorDescription({
            message: ERROR_MESSAGE_DETAILS_PAGE__500_ALERT,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    getData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!data) return;

    const currentTimestamp = DateTime.local().toMillis();
    const validToTimestamp = DateTime.fromISO(data?.valid_to).toMillis();
    const timeDiff = validToTimestamp - currentTimestamp;
    setInvalid(timeDiff <= 0);

    const descriptions = [];

    if (data?.description) {
      descriptions.push({
        type: 'paragraph',
        content: data.description,
      });
    }

    if (data?.custom_description) {
      data.custom_description.forEach((custom) => {
        descriptions.push(custom);
      });
    }

    if (data?.videos && data.videos.length > 0) {
      data.videos.forEach((link) =>
        descriptions.push({
          type: 'video',
          content: link.url,
        }),
      );
    }

    setCustomDescription(descriptions);
  }, [data]);

  const getIconColors = useCallback(
    (subtype) => {
      if (subtype === 'notice') {
        return {
          circleColor: '#139ECA',
          fillColor: theme.colors.white,
        };
      }
      return {
        circleColor: theme.colors.warning,
        fillColor: theme.colors.white,
      };
    },
    [theme],
  );

  const updatedDate = DateTime.fromISO(data?.updated_at).toRelative();
  const issuedDate = DateTime.fromISO(data?.created_at).toRelative();

  const handleBack = useCallback(() => {
    history.push('/maps/notifications/alerts');
  }, [history]);

  const handleShare = useCallback(() => {
    history.push('/maps/share', {
      sharePath: sharingPath,
      shareText: 'Check out this alert on Deckee!',
      title: data?.title,
      description: data?.description,
      type: 'alert',
      id,
    });
  }, [data?.description, data?.title, history, id, sharingPath]);

  return (
    <BasicDrawerLayout heading="Alert" onBack={handleBack} onClose={null}>
      <LayoutBody>
        {isLoading && <LogoSpinner background={false} />}

        {errorDescription && (
          <ErrorMessage description={errorDescription?.message} />
        )}

        {!isLoading && !errorDescription && data && (
          <>
            {/* Expired alert banner */}
            {invalid && (
              <Box mb="2">
                <MessageBlock type="warning" style={{ borderRadius: '12px' }}>
                  {ERROR_MESSAGE_DETAILS_PAGE__ALERT_EXPIRED}
                </MessageBlock>
              </Box>
            )}

            {/* Alert details card */}
            <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
              <Card.Body pb="0">
                {/* Centered alert icon */}
                <Box display="flex" justifyContent="center" pt="2" pb="3">
                  <ReactSvg
                    toDisplay={getWeatherAlertIcon(data?.subtype || data?.type)}
                    options={getIconColors(data?.subtype)}
                    width={62}
                    height={62}
                  />
                </Box>

                {/* Alert title */}
                <Heading level={5} textAlign="center" mb="2">
                  {data?.title}
                </Heading>

                {/* Updated date */}
                <Paragraph textAlign="center" mb="3">
                  {updatedDate
                    ? `Updated ${updatedDate}`
                    : `Created ${issuedDate}`}
                </Paragraph>

                {/* Alert description content */}
                {customDescription.length > 0 && (
                  <Box mb="3" color="black">
                    <CustomDescription description={customDescription} />
                  </Box>
                )}

                {/* Source */}
                {data?.source && (
                  <Paragraph color="darkergrey" mb="3">
                    Source: {data.source}
                  </Paragraph>
                )}
              </Card.Body>

              {/* Share footer */}
              <CardFooter>
                <FooterLink onClick={handleShare}>
                  <Box display="flex" alignItems="center">
                    <ReactSvg
                      toDisplay={sendArrow()}
                      options={{ fillColor: theme.colors.primary }}
                      width={20}
                      height={20}
                    />
                    <Box
                      ml="1"
                      fontSize="15px"
                      fontWeight="bold"
                      color="primary"
                    >
                      Share
                    </Box>
                  </Box>
                </FooterLink>
              </CardFooter>
            </Card>
          </>
        )}
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

AlertDetailsContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

const mapDispatchToProps = () => ({});
const mapStateToProps = () => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AlertDetailsContainer);
